import type { SavedView } from '@ft/shared';
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useI18n } from '../i18n';
import { api } from '../lib/http';

/** Knopf „Ansichten“ auf Listenseiten: aktuelle Filter unter einem Namen speichern, gespeicherte öffnen oder löschen. */
export function SavedViews() {
  const { t } = useI18n();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [views, setViews] = useState<SavedView[]>([]);
  const [name, setName] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const box = useRef<HTMLDivElement>(null);
  const path = `${loc.pathname}${loc.search}`;

  useEffect(() => { if (open) api<SavedView[]>('GET', '/api/views').then(setViews, () => undefined); }, [open]);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (!box.current?.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close); window.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); window.removeEventListener('keydown', esc); };
  }, [open]);

  async function save() {
    if (!name.trim()) return;
    setErr(null);
    try { setViews(await api<SavedView[]>('POST', '/api/views', { name: name.trim(), path })); setName(''); } catch (e) { setErr((e as Error).message); }
  }
  const here = views.filter((v) => v.path.split('?')[0] === loc.pathname);
  const other = views.filter((v) => v.path.split('?')[0] !== loc.pathname);

  return (
    <div className="views" ref={box}>
      <button className="btn" aria-expanded={open} aria-haspopup="true" onClick={() => setOpen((o) => !o)}>
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3l2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17l-5.4 2.9 1-6.1L3.2 9.5l6.1-.9z" /></svg>{t('views.button')}
      </button>
      {open && (
        <div className="views-menu" role="menu">
          <form className="views-save" onSubmit={(e) => { e.preventDefault(); void save(); }}>
            <label htmlFor="views-name">{t('views.saveCurrent')}</label>
            <div><input id="views-name" value={name} onChange={(e) => setName(e.target.value)} placeholder={t('views.namePh')} maxLength={60} autoFocus /><button className="btn primary" disabled={!name.trim()}>{t('common.save')}</button></div>
            {err && <p className="err small">{err}</p>}
          </form>
          {[...here, ...other].length === 0 ? <p className="muted small views-empty">{t('views.empty')}</p> : (
            <ul>
              {[...here, ...other].map((v) => (
                <li key={v.id} className={v.path === path ? 'cur' : undefined}>
                  <Link to={v.path} onClick={() => setOpen(false)} role="menuitem">{v.name}{!here.includes(v) && <span className="muted small"> · {t(`views.page.${v.path.split('?')[0]!.slice(1)}` as never)}</span>}</Link>
                  <button className="x" aria-label={t('views.delete', { n: v.name })} onClick={async () => setViews(await api<SavedView[]>('DELETE', `/api/views/${v.id}`))}>×</button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
